import React, { useEffect, useMemo, useState } from "react";
import {
  DUAL_ACCOUNT_TYPES,
  getDualConfig,
  calculateDualResult,
  applyDualPrices,
} from "../pricing/dual";
import { publicApi } from "../lib/api";
import { SITE_CONFIG } from "../data/config";
import {
  Calculator as CalcIcon,
  CheckCircle2,
  Coins,
  Info,
  RefreshCw,
  Sparkles,
  MessageCircle,
  Layers,
  CalendarClock,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import SuggestionsPanel from "./SuggestionsPanel";

const formatNum = (n) => new Intl.NumberFormat("en-US").format(n);

const SELECTION_STYLES = {
  regular: {
    label: "اعتيادي",
    color: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40",
    dot: "bg-emerald-400",
    btn: "bg-emerald-500 text-white border-emerald-400",
    btnIdle: "bg-white/[0.03] text-emerald-300 border-white/10 hover:border-emerald-500/40",
  },
  advanced: {
    label: "متقدم",
    color: "bg-orange-500/15 text-orange-200 border-orange-500/40",
    dot: "bg-orange-400",
    btn: "bg-orange-500 text-white border-orange-400",
    btnIdle: "bg-white/[0.03] text-orange-300 border-white/10 hover:border-orange-500/40",
  },
  pulled: {
    label: "مسحوبة مسبقاً",
    color: "bg-slate-700/40 text-slate-300 border-slate-600/40",
    dot: "bg-slate-400",
    btn: "bg-slate-500 text-white border-slate-400",
    btnIdle: "bg-white/[0.03] text-slate-300 border-white/10 hover:border-slate-500/40",
  },
};

const NEUTRAL_STYLE = {
  color: "bg-white/[0.02] border-white/10 text-slate-300",
  dot: "bg-slate-500/60",
};

const DualDrawCalculator = ({ type, title, subtitle }) => {
  const config = getDualConfig(type);
  const [region, setRegion] = useState(DUAL_ACCOUNT_TYPES[0].id);
  const [drawStates, setDrawStates] = useState(() =>
    Array.from({ length: config.totalDraws }, () => ({ selection: null }))
  );
  const [currentCP, setCurrentCP] = useState(0);
  const [discountPct, setDiscountPct] = useState(0);
  const [pricesVersion, setPricesVersion] = useState(0);

  useEffect(() => {
    publicApi
      .get("/calculator/dual-prices")
      .then(({ data }) => applyDualPrices(data) && setPricesVersion((v) => v + 1))
      .catch(() => {});
  }, []);

  const result = useMemo(
    () => calculateDualResult(type, region, drawStates, currentCP, discountPct),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, region, drawStates, currentCP, discountPct, pricesVersion]
  );

  const setDrawSelection = (idx, selection) => {
    setDrawStates((prev) => {
      const next = [...prev];
      const current = next[idx].selection;
      next[idx] = { selection: current === selection ? null : selection };
      return next;
    });
  };

  const applyPlan = () => {
    const cfg = getDualConfig(type);
    const plan = cfg?.recommendedPlan?.states;
    if (!plan) return;
    setDrawStates(plan.map((s) => ({ selection: s })));
  };

  const applyAllAdvanced = () => {
    setDrawStates(
      Array.from({ length: config.totalDraws }, () => ({ selection: "advanced" }))
    );
  };

  const resetAll = () => {
    setDrawStates(
      Array.from({ length: config.totalDraws }, () => ({ selection: null }))
    );
    setCurrentCP(0);
    setDiscountPct(0);
  };

  const buildWhatsappMessage = () => {
    if (!result) return "";
    const accName =
      DUAL_ACCOUNT_TYPES.find((a) => a.id === region)?.label || region;
    const lines = [
      `مرحباً Rush Store 👋`,
      `حساب ${title}:`,
      ``,
      `▪️ نوع الحساب: ${accName}`,
      `▪️ إجمالي السحبات في العجلة: ${config.totalDraws}`,
      `▪️ الإجمالي الرسمي للخطة: ${formatNum(result.totalPlan)} CP`,
      ``,
      `✅ سحبات اعتيادي: ${result.regularCount}`,
      `🔶 سحبات متقدم: ${result.advancedCount}`,
      `⏹ مسحوبة مسبقاً: ${result.pulledCount}`,
      `⚪ غير محددة: ${result.unselectedCount}`,
      ``,
      `🎯 الإجمالي الحالي: ${formatNum(result.remainingDrawCost)} CP`,
      `💰 رصيد CP الحالي: ${formatNum(result.balance)}`,
      `🛒 صافي CP المطلوب شراؤه: ${formatNum(result.requiredCpToBuy)}`,
      ``,
      `📅 آخر تحديث: ${SITE_CONFIG.pricesLastUpdated}`,
    ];
    return encodeURIComponent(lines.join("\n"));
  };

  const waLink = `https://wa.me/${SITE_CONFIG.whatsappNumber}?text=${buildWhatsappMessage()}`;

  if (!config || !result) return null;

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="rounded-3xl bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 backdrop-blur-xl shadow-2xl shadow-blue-900/30 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 sm:px-8 py-5 border-b border-white/10 bg-blue-600/10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] flex items-center justify-center shadow-lg">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-white font-black text-lg sm:text-xl leading-tight">
                {title}
              </h2>
              <p className="text-white/50 text-xs">{subtitle}</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] text-white/60 bg-white/[0.04] border border-white/10 rounded-lg px-2.5 py-1.5">
            <CalendarClock className="w-3.5 h-3.5 text-[#C9A96A]" />
            <span>آخر تحديث: {SITE_CONFIG.pricesLastUpdated}</span>
          </div>
        </div>

        <div className="p-5 sm:p-8 space-y-6">
          {/* Account type */}
          <div>
            <Label className="text-white/80 text-sm mb-3 block">
              نوع الحساب
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {DUAL_ACCOUNT_TYPES.map((t) => {
                const active = region === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setRegion(t.id)}
                    className={`px-2 py-3 rounded-xl border text-sm font-semibold transition-all ${
                      active
                        ? "bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] border-[#B08A4A] shadow-lg shadow-[#B08A4A]/25"
                        : "bg-white/[0.03] text-white border-white/10 hover:border-[#B89968]/40"
                    }`}
                  >
                    <div className="text-[13px] leading-tight">{t.label}</div>
                    <div
                      className={`text-[10px] mt-0.5 ${
                        active ? "text-[#0A1128]/70" : "text-white/40"
                      }`}
                    >
                      {t.subtitle}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recommended plan block */}
          <div className="rounded-2xl bg-[#C9A96A]/[0.05] border border-[#C9A96A]/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#D4B98A]" />
              <div className="text-white font-bold text-sm">
                ⚡ الخطة الموصى بها لتقليل التكلفة
              </div>
            </div>
            {(() => {
              const plan = config?.recommendedPlan;
              if (!plan?.states) return null;
              // Build human-readable ranges per state
              const groups = [];
              let cur = { state: plan.states[0], from: 1, to: 1 };
              for (let i = 1; i < plan.states.length; i++) {
                if (plan.states[i] === cur.state) {
                  cur.to = i + 1;
                } else {
                  groups.push(cur);
                  cur = { state: plan.states[i], from: i + 1, to: i + 1 };
                }
              }
              groups.push(cur);
              const stateLabel = (s) =>
                s === "advanced"
                  ? { txt: `متقدم (${type === "weapons" ? "السلاحين مع بعض" : "الشخصيتين مع بعض"})`, color: "text-orange-300" }
                  : { txt: "اعتيادي", color: "text-emerald-300" };
              const rangeLabel = (g) =>
                g.from === g.to ? `السحبة ${g.from}` : `السحبات ${g.from} إلى ${g.to}`;
              return (
                <ul className="text-slate-200 text-xs sm:text-sm space-y-1 mb-3" data-testid="dual-plan-list">
                  {groups.map((g, idx) => {
                    const s = stateLabel(g.state);
                    return (
                      <li key={idx}>
                        <span className="text-[#D4B98A]">✿</span> {rangeLabel(g)}:{" "}
                        <span className={`${s.color} font-bold`}>{s.txt}</span>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}

            <div className="flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                onClick={applyPlan}
                data-testid="dual-apply-plan"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] text-[#0A1128] font-bold text-xs hover:brightness-110 transition"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                ⚡ تطبيق الخطة
              </button>
              <button
                type="button"
                onClick={applyAllAdvanced}
                data-testid="dual-all-advanced"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-slate-200 font-semibold text-xs transition"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                كل السحبات متقدم
              </button>
              <button
                type="button"
                onClick={resetAll}
                data-testid="dual-reset"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-slate-200 font-semibold text-xs transition"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                إعادة التعيين
              </button>
            </div>

            <div className="flex items-start gap-2 text-[11px] text-orange-200/90 bg-orange-500/8 border border-orange-500/25 rounded-lg px-3 py-2.5 leading-relaxed">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-orange-300" />
              <span>
                <span className="font-bold">⚠️ ملاحظة هامة:</span> أثناء السحب في العجلة، وبعد كل مرة تختار شراء "اعتيادي"، قد تظهر لك نافذة تقترح شراء الغرض الثاني من نفس السحبة. اضغط "إلغاء"، ثم أكمل السحب مرة أخرى من خيار "شراء اعتيادي". كرر ذلك حتى تصل للسحبة الأخيرة حسب الخطة.
              </span>
            </div>
          </div>

          {/* Draws list */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-white/80 text-sm">
                حدد نوع كل سحبة يدوياً
              </Label>
              <span className="text-[11px] text-white/50">
                محددة: {result.selectedCount}/{config.totalDraws}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {result.breakdown.map((d, i) => {
                const sel = d.selection;
                const wrapStyle = sel
                  ? SELECTION_STYLES[sel].color
                  : `${NEUTRAL_STYLE.color}`;
                const displayCost = sel === "advanced" ? d.advancedCost : d.regularCost;
                return (
                  <div
                    key={d.draw}
                    data-testid={`dual-draw-${d.draw}`}
                    className={`rounded-xl border p-3 ${wrapStyle} transition-colors`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-[#0A1128]/60 text-white font-black text-xs flex items-center justify-center">
                          {d.draw}
                        </span>
                        <div>
                          <div className="text-xs font-bold">السحبة #{d.draw}</div>
                          {sel && (
                            <div className="flex items-center gap-1 mt-0.5 text-[10px] opacity-90">
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${SELECTION_STYLES[sel].dot}`}
                              />
                              <span>{SELECTION_STYLES[sel].label}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-end">
                        <div
                          className={`font-black text-sm ${
                            sel === "pulled" || sel === null
                              ? "opacity-60 line-through"
                              : ""
                          }`}
                        >
                          {formatNum(displayCost)} CP
                        </div>
                        <div className="text-[9px] opacity-70">
                          {sel === "pulled"
                            ? "لا تدخل في الحساب"
                            : sel === null
                            ? "غير محددة"
                            : `تراكمي: ${formatNum(d.cumulative)}`}
                        </div>
                      </div>
                    </div>

                    {/* Selection buttons — يظهر السعر لكل حالة */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <button
                        type="button"
                        data-testid={`dual-draw-${d.draw}-regular`}
                        onClick={() => setDrawSelection(i, "regular")}
                        className={`px-2 py-2 rounded-lg text-[10px] font-bold border transition-all flex flex-col items-center leading-tight ${
                          sel === "regular" ? SELECTION_STYLES.regular.btn : SELECTION_STYLES.regular.btnIdle
                        }`}
                      >
                        <span>اعتيادي</span>
                        <span className="text-[9px] opacity-80 mt-0.5">{formatNum(d.regularCost)} CP</span>
                      </button>
                      <button
                        type="button"
                        data-testid={`dual-draw-${d.draw}-advanced`}
                        onClick={() => setDrawSelection(i, "advanced")}
                        className={`px-2 py-2 rounded-lg text-[10px] font-bold border transition-all flex flex-col items-center leading-tight ${
                          sel === "advanced" ? SELECTION_STYLES.advanced.btn : SELECTION_STYLES.advanced.btnIdle
                        }`}
                      >
                        <span>متقدم</span>
                        <span className="text-[9px] opacity-80 mt-0.5">{formatNum(d.advancedCost)} CP</span>
                      </button>
                      <button
                        type="button"
                        data-testid={`dual-draw-${d.draw}-pulled`}
                        onClick={() => setDrawSelection(i, "pulled")}
                        className={`px-2 py-2 rounded-lg text-[10px] font-bold border transition-all flex flex-col items-center leading-tight ${
                          sel === "pulled" ? SELECTION_STYLES.pulled.btn : SELECTION_STYLES.pulled.btnIdle
                        }`}
                      >
                        <span>مسحوبة</span>
                        <span className="text-[9px] opacity-80 mt-0.5">0 CP</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current CP + Discount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/80 text-sm mb-2 block">
                رصيد CP الحالي
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  value={currentCP}
                  onChange={(e) => setCurrentCP(e.target.value)}
                  data-testid="dual-balance"
                  className="h-12 bg-white/[0.03] border-white/10 text-white text-base focus-visible:ring-[#C9A96A]/40 ps-12"
                  placeholder="0"
                />
                <Coins className="w-5 h-5 absolute start-3 top-1/2 -translate-y-1/2 text-[#C9A96A]" />
              </div>
            </div>
            <div>
              <Label className="text-white/80 text-sm mb-2 block">
                الخصم على العجلة (%)
              </Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
                data-testid="dual-discount"
                className="h-12 bg-white/[0.03] border-white/10 text-white text-base focus-visible:ring-[#C9A96A]/40"
                placeholder="0"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard
              label="الإجمالي الحالي"
              value={formatNum(result.remainingDrawCost)}
              suffix="CP"
              accent="gold"
              highlight={result.remainingDrawCost > 0}
            />
            <SummaryCard
              label="اعتيادي / متقدم"
              value={`${result.regularCount} / ${result.advancedCount}`}
              accent="blue"
            />
            <SummaryCard
              label="مسحوبة مسبقاً"
              value={result.pulledCount}
              accent="slate"
            />
            <SummaryCard
              label="صافي المطلوب شراؤه"
              value={formatNum(result.requiredCpToBuy)}
              suffix="CP"
              accent="red"
              highlight={result.requiredCpToBuy > 0}
            />
          </div>

          <div className="flex items-start gap-2 text-[11px] text-white/50 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              مرجع الأسعار: كل السحبات اعتيادي ={" "}
              <span className="text-emerald-300 font-bold">{formatNum(result.totalRegular)} CP</span>
              {"  ·  "}
              كل السحبات متقدم ={" "}
              <span className="text-orange-300 font-bold">{formatNum(result.totalAdvanced)} CP</span>
              {result.discountPct > 0 && (
                <span>
                  {"  ·  "}
                  خصم مطبَّق: <span className="text-[#E5CB94] font-bold">{Math.round(result.discountPct)}%</span>
                </span>
              )}
            </span>
          </div>

          {result.balanceCoversAll && (
            <div className="flex items-start gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 text-emerald-200 text-sm">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>رصيدك الحالي يكفي لإكمال ما اخترته! لا تحتاج لشراء إضافي.</span>
            </div>
          )}

          {/* Smart Suggestions */}
          {result.requiredCpToBuy > 0 && (
            <SuggestionsPanel neededCP={result.requiredCpToBuy} />
          )}

          {/* WhatsApp */}
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            إرسال النتيجة عبر الواتساب
          </a>
        </div>
      </div>
    </section>
  );
};

const SummaryCard = ({ label, value, suffix, accent = "blue", highlight }) => {
  const accents = {
    blue: "from-blue-500/20 to-blue-600/5 border-blue-500/30",
    gold: "from-[#C9A96A]/20 to-[#B08A4A]/5 border-[#C9A96A]/30",
    slate: "from-slate-500/20 to-slate-600/5 border-slate-500/30",
    red: "from-red-500/20 to-red-600/5 border-red-500/30",
  };
  return (
    <div
      className={`rounded-2xl bg-gradient-to-br ${accents[accent]} border p-3 ${
        highlight ? "ring-1 ring-white/10" : ""
      }`}
    >
      <div className="text-xs text-white/60">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <div className="text-white font-black text-xl sm:text-2xl">{value}</div>
        {suffix && (
          <div className="text-white/60 text-xs font-semibold">{suffix}</div>
        )}
      </div>
    </div>
  );
};

export default DualDrawCalculator;
